// The light the exposure is set from: how bright the eye's world is, from the tables' light at the eye and how much of the world around it is planet. No three.js.
//
// The display gain (exposure.ts) is a function of one luminance. On the ground it is the luminance of the open-ground illumination: the sky's light plus the star's beam on a horizontal surface,
// which is what a lit scene is, since the scene is mostly ground and sky and both are lit by those two. Climbing, the scene stops being that: the horizontal surface under the eye is
// a smaller part of what surrounds it (the planet shrinks toward a ball below), and what faces the star has the beam whole. So the beam's weight in the scene is the cosine of the horizontal
// surface (sin of the star's elevation) for the share of the sphere around the eye that is planet, and 1 (a surface facing the star: a hull, a moon, a cloud top) for the rest:
//
//     surface  =  sky  +  ( share * max(sin el, 0)  +  (1 - share) ) * beam
//
// `share` is the planet's solid angle over the half-sphere that is 1 on the ground (the planet fills the lower half, the horizon dip aside): 1 - cos(a), a the planet's angular radius, which
// falls smoothly to 0 as the eye goes out (0.9 at 30 km, 0.66 at 400 km on a planet like this one, 0.02 at geostationary height). No air enters it, and nothing is blended between two regimes: on
// the ground it is the old measure exactly, far from every body it is the light on a surface facing the star, and an eye in the planet's shadow gets none of the beam because the tables give
// it none (the beam is zero where the planet covers the star).
//
// The other number is for things seen against the sky and not on a lit surface: the stars and the Milky Way. The eye that looks at them has the sky's light to adapt to, and not the beam
// on the ground below it, so their gain is set from the sky's own light (`dark`): by day on the ground that is the blue sky, which hides them as it should; at the top of the air it is
// the floor of the night (airglow, starlight, the moon) whatever the sun is doing below, which is why they show above the air in daylight and not under the sky.
export interface EyeLum {
  /** The luminance the exposure of lit surfaces is set from. */
  surface: number;
  /** The luminance the exposure of things seen against the sky (stars, the galaxy) is set from. */
  dark: number;
}

/**
 * The planet's share of the sphere around the eye, doubled so that it is 1 on the ground.
 * @param radius the body's radius (m)
 * @param dist   the eye's distance from the body's centre (m)
 */
export function groundShare(radius: number, dist: number): number {
  if (!(radius > 0) || !(dist > 0)) return 0;
  const s = Math.min(1, radius / dist);
  return 1 - Math.sqrt(Math.max(0, 1 - s * s));
}

/**
 * @param sky    luminance of the diffuse light on an open horizontal surface at the eye (sky, floor of the night); 0 where there is no air to hold any
 * @param beam   luminance of the star's beam normal to itself at the eye (0 in the planet's shadow)
 * @param sinEl  the sine of the star's elevation above the eye's horizontal
 * @param share  groundShare
 */
export function eyeLum(sky: number, beam: number, sinEl: number, share: number): EyeLum {
  const w = Math.min(1, Math.max(0, share));
  const surface = sky + (w * Math.max(0, sinEl) + (1 - w)) * beam;
  return { surface, dark: sky };
}
